import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { Icon } from "@circle-react/components/shared/Icon";
import { SidebarBadge } from "@circle-react/components/Spaces/SidebarV2/PlatformMenu/SidebarBadge";
import { shouldRenderLockIcon } from "@circle-react/components/Spaces/utils";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isIntendedOnNewTab } from "@circle-react/helpers/browserHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useResetSpaceSidebarNotifications } from "@circle-react/hooks/useSpaceSidebarNotifications";
import type { Space } from "@circle-react/types";
import { Link } from "@tanstack/react-router";
import { forwardRef } from "react";

import { useThemeContext } from "@/react/providers/Theme";

import { useGetSidebarLinkClass } from "./useGetSidebarLinkClass";

export interface SpaceViewProps {
  buttonLink?: string;
  className?: string;
  isDragOverlay?: boolean;
  space: Partial<Space>;
  spaceNotificationDetail?: {
    unread_content_count: number;
    unread_notifications_count: number;
  };
  style?: React.CSSProperties;
}

export const SpaceView = forwardRef<HTMLDivElement, SpaceViewProps>(
  (
    {
      space,
      buttonLink = "/",
      spaceNotificationDetail = {
        unread_content_count: 0,
        unread_notifications_count: 0,
      },
      style,
      className,
      isDragOverlay = false,
      ...rest
    },
    ref,
  ) => {
    const { getActiveInactiveClasses, getUnreadClasses } =
      useGetSidebarLinkClass();
    const { currentCommunityMember } = usePunditUserContext();
    const isAdmin = isCommunityAdmin(currentCommunityMember);
    const { currentAppearance } = useThemeContext();

    const { unread_content_count, unread_notifications_count = 0 } =
      spaceNotificationDetail ?? {};

    const hasUnreadContent = unread_content_count > 0;
    const hasNotifications = unread_notifications_count > 0;
    const shouldUseLockIcon = shouldRenderLockIcon({
      shouldShowLockIconForNonMembers:
        space.show_lock_icon_for_non_members && !space.is_space_member,
      isPrivate: space.is_private,
      isAdmin,
    });

    const { resetUnreadCount } = useResetSpaceSidebarNotifications(space.id);

    return (
      <div
        ref={ref}
        style={style}
        className={className}
        draggable="false"
        {...rest}
        tabIndex={-1}
      >
        <Link
          to={buttonLink || "/"}
          className={classNames(
            "focus-visible:outline-secondary select-none h-8.5 group flex items-center gap-2 !rounded-md px-4 py-1.5 transition-colors duration-75 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
            getUnreadClasses(hasUnreadContent),
            { "!bg-c-sidebar-hover !text-c-sidebar": isDragOverlay },
          )}
          activeOptions={{ includeSearch: false }}
          activeProps={{
            className: getActiveInactiveClasses(true),
          }}
          inactiveProps={{
            className: getActiveInactiveClasses(false),
          }}
          draggable="false"
          data-id={space.id}
          onClick={e => {
            if (isIntendedOnNewTab(e)) {
              void resetUnreadCount();
            }
          }}
        >
          <span className="flex">
            <EmojiRenderer
              className="flex !h-5 !max-h-5 !w-5 max-w-none items-center leading-none"
              emojiClassName="text-sm justify-center"
              emoji={space.emoji}
              appearance={currentAppearance}
              customEmojiUrl={space.custom_emoji_url}
              customEmojiDarkUrl={space.custom_emoji_dark_url}
              shouldUseLockIcon={shouldUseLockIcon}
              lockIcon={<Icon size={16} type="lock-v2" />}
            />
          </span>
          <span className="truncate">
            <h4
              className={classNames("text-sm truncate", {
                "font-medium": !hasUnreadContent,
                "font-semibold": hasUnreadContent,
              })}
              data-testid="space-name"
            >
              {space.name}
            </h4>
          </span>
          {(hasUnreadContent || hasNotifications) && (
            <SidebarBadge isGray={!hasNotifications}>
              {unread_notifications_count > 0
                ? unread_notifications_count
                : unread_content_count > 99
                  ? "99+"
                  : unread_content_count}
            </SidebarBadge>
          )}
        </Link>
      </div>
    );
  },
);

SpaceView.displayName = "SpaceView";
